import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ReactApexChart from "react-apexcharts";
import { getFormatedDate } from "../../../../helper";
import { makeStyles } from "@material-ui/core/styles";
import { setPastDueSTOCheck } from "../../../../store/Actions";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const DetentionMultiLineBarChart = ({
  activeTab,
  graphData = [],
  type,
  benchmarkValue,
}) => {
  const classes = useStyles();
  const [borderRadius, setBorderRadius] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const itemsPerPage = 5;
  const dispatch = useDispatch();
  const safeGraphData = Array.isArray(graphData) ? graphData : [];
  const graphConfig = {
    IncidentRateTrend: {
      name: "IncidentRateTrend",
      shouldCalculateMax: true,
      firstMaxKey: "Cy_Total_Detained_Shipment",
      secondMaxKey: "Py_Total_Detained_Shipment",
      categoryKey: "Year_week",
      seriesData: [
        {
          name: "Incident Rate this year",
          type: "line",
          data: graphData?.map((item) => item?.Cy_Incident_rate || 0),
        },
        {
          name: "Incident Rate last year",
          type: "line",
          data: graphData?.map((item) => item?.Py_Incident_rate || 0),
        },
        {
          name: "Detained Shipment this year",
          type: "bar",
          data: graphData?.map((item) => item?.Cy_Total_Detained_Shipment || 0),
        },
        {
          name: "Detained Shipment last year",
          type: "bar",
          data: graphData?.map((item) => item?.Py_Total_Detained_Shipment || 0),
        },
      ],
      yAxisTitle: "Shipments Detained",
      yAxisSeriesName: [
        "Detained Shipment this year",
        "Detained Shipment last year",
      ],
      rightYAxis: {
        // Right y-axis for Incident Rate this year (percentage)
        opposite: true, // Place the axis on the right side
        seriesName: ["Incident Rate this year", "Incident Rate last year"],
        title: {
          text: "Incident Rate %",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => `${value.toFixed(0)}%`, // Format percentage on the right y-axis
        },
        min: 0, // Set minimum value for right y-axis
        max: 40, // Set maximum value for right y-axis
      },
    },
    DetentionSpendTrend: {
      name: "DetentionSpendTrend",
      shouldCalculateMax: true,
      firstMaxKey: "Detention_cost_cy",
      secondMaxKey: "Detention_cost_ly",
      categoryKey: "year_week_cy",
      seriesData: [
        {
          name: "Detention spend % this year",
          type: "line",
          data: graphData?.map((item) => item?.Detention_spend_cy || 0),
        },
        {
          name: "Detention spend % last year",
          type: "line",
          data: graphData?.map((item) => item?.Detention_spend_ly || 0),
        },
        {
          name: "Detention spend cost this year",
          type: "bar",
          data: graphData?.map((item) => item?.Detention_cost_cy || 0),
        },
        {
          name: "Detention spend cost last year",
          type: "bar",
          data: graphData?.map((item) => item?.Detention_cost_ly || 0),
        },
      ],
      yAxisTitle: "Detention spend in $",
      yAxisSeriesName: [
        "Detention spend cost this year",
        "Detention spend cost last year",
      ],
      rightYAxis: {
        opposite: true, // Place the axis on the right side
        seriesName: [
          "Detention spend % this year",
          "Detention spend % last year",
        ],
        title: {
          text: "Transport Spend %",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => `${value.toFixed(0)}%`, // Format percentage on the right y-axis
        },
        min: 0, // Set minimum value for right y-axis
        max: 20, // Set maximum value for right y-axis
      },
    },
    FacilityTypeLocationShare: {
      name: "FacilityTypeLocationShare",
      shouldCalculateMax: true,
      firstMaxKey: "Cy_Detention_cost",
      secondMaxKey: "Ly_Detention_cost",
      categoryKey: "Value",
      seriesData: [
        {
          name: "Incident cost share this year",
          type: "line",
          data: graphData?.map((item) => item?.Cy_Detention_share || 0),
        },
        {
          name: "Incident cost share last year",
          type: "line",
          data: graphData?.map((item) => item?.Ly_Detention_share || 0),
        },
        {
          name: "Detention cost this year",
          type: "bar",
          data: graphData?.map((item) => item?.Cy_Detention_cost || 0),
        },
        {
          name: "Detention cost last year",
          type: "bar",
          data: graphData?.map((item) => item?.Ly_Detention_cost || 0),
        },
      ],
      yAxisTitle: "Shipments Detained in $",
      yAxisSeriesName: ["Detention cost this year", "Detention cost last year"],
      rightYAxis: {
        opposite: true, // Place the axis on the right side
        seriesName: [
          "Incident cost share this year",
          "Incident cost share last year",
        ],
        title: {
          text: "Incident cost share %",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => `${value.toFixed(0)}%`, // Format percentage on the right y-axis
        },
        min: 0, // Set minimum value for right y-axis
        max: 40, // Set maximum value for right y-axis
      },
    },
    DetentionReasonsCodes: {
      name: "DetentionReasonsCodes",
      shouldCalculateMax: true,
      firstMaxKey: "Customer_Detention_Cost",
      secondMaxKey: "Nestle_DC_Controllable_Detention_Cost",
      categoryKey: "EditReasonCode",
      seriesData: [
        {
          name: "Customer location",
          type: "bar",
          data: graphData?.map((item) => item?.Customer_Detention_Cost || 0),
        },
        {
          name: "Nestle DC",
          type: "bar",
          data: graphData?.map(
            (item) => item?.Nestle_DC_Controllable_Detention_Cost || 0
          ),
        },
      ],
      yAxisTitle: "Detention cost $k",
      yAxisSeriesName: ["Customer location", "Nestle DCr"],
    },
  };
  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(2);
      } else if (screenWidth < 1500) {
        setBorderRadius(5);
      } else {
        setBorderRadius(7);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    calculateMaximunValue();
  }, [graphData, type]);

  const calculateMaximunValue = () => {
    const shipmentThisYearData = graphData?.map(
      (item) => item?.[graphConfig?.[activeTab]?.firstMaxKey] || 0
    );
    const shipmentLastYearData = graphData?.map(
      (item) => item?.[graphConfig?.[activeTab]?.secondMaxKey] || 0
    );
    const maxShipmentThisYear = Math.max(...shipmentThisYearData);
    const maxShipmentLastYear = Math.max(...shipmentLastYearData);

    if (maxShipmentThisYear > maxShipmentLastYear) {
      setMaxValue(maxShipmentThisYear);
    } else {
      setMaxValue(maxShipmentLastYear);
    }
  };

  const formatToKOrM = (value) => {
    if (value >= 1e6) {
      return `${(value / 1e6)?.toFixed(0)}M`;
    } else if (value >= 1e3) {
      return `${(value / 1e3)?.toFixed(0)}K`;
    }
    return value?.toFixed(0);
  };

  const totalItems = graphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Determine the categories, series, and yAxisTitle dynamically based on activeTab
  let categories = [];
  let seriesData = [];
  let yAxisTitle = "";

  categories = graphData?.map(
    (item) => item?.[graphConfig?.[activeTab]?.categoryKey] || "Unknown Metric"
  );
  seriesData = graphConfig?.[activeTab]?.seriesData;
  yAxisTitle = graphConfig?.[activeTab]?.yAxisTitle;

  // Slice data for current page
  const slicedCategories = categories?.slice(startIndex, endIndex);
  const slicedSeries = seriesData?.map((s) => ({
    ...s,
    data: s.data.slice(startIndex, endIndex),
  }));

  let options = {
    chart: {
      id: "detention-chart",
      height: 350,
      type: "bar",
      stacked: false,
      animations: { enabled: false },
      zoom: { enabled: false },
      toolbar: {
        show: false,
      },
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const clickedBarIndex = config.dataPointIndex;
          const clickedBarData = safeGraphData[clickedBarIndex];
          const apiData = {
            selectedOption: clickedBarData,
            PastDueFlag: true,
            tab: activeTab,
          };
          dispatch(setPastDueSTOCheck(apiData));
          // if (
          //   activeTab === "ST Utilization" ||
          //   activeTab === "LT Utilization" ||
          //   activeTab === "STRs Required"
          // ) {
          //   dispatch(setPastDueSTOCheck(apiData));
          // }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "14%",
        borderRadius: borderRadius,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 3,
      curve: "straight",
    },
    series: seriesData,
    colors: ["#A4D65D", "#EC6FBD", "#0067A1", "#F7D240"],
    xaxis: {
      categories: slicedCategories,
      tickAmount: slicedCategories.length,
      tickPlacement: "on",
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        cssClass: "force-xaxis-rotate",
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
    },
    yaxis: [
      {
        seriesName: graphConfig?.[activeTab]?.yAxisSeriesName,
        title: {
          text: yAxisTitle,
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) =>
            activeTab === "DetentionSpendTrend" ||
            activeTab === "DetentionReasonsCodes" ||
            activeTab === "FacilityTypeLocationShare"
              ? `$${formatToKOrM(value)}`
              : formatToKOrM(value),
        },
        min: 0, // Set minimum value for right y-axis
        max: maxValue, // Set maximum value for right y-axis
      },
      // {
      //   // Right y-axis for Incident Rate this year (percentage)
      //   opposite: true, // Place the axis on the right side
      //   seriesName: ["Incident Rate this year", "Incident Rate last year"],
      //   title: {
      //     text: "Incident Rate %",
      //     style: {
      //       fontSize: "12px",
      //       fontFamily: "Poppins, sans-serif",
      //       fontWeight: 500,
      //       color: "#333",
      //     },
      //   },
      //   labels: {
      //     formatter: (value) => `${value.toFixed(0)}`, // Format percentage on the right y-axis
      //   },
      //   min: 0, // Set minimum value for right y-axis
      //   max: 100, // Set maximum value for right y-axis
      // },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (value) =>
            activeTab === "DetentionReasonsCodes"
              ? `$${formatToKOrM(value)}`
              : `${formatToKOrM(value)}%`,
        },
        {
          formatter: (value) =>
            activeTab === "DetentionReasonsCodes"
              ? `$${formatToKOrM(value)}`
              : `${formatToKOrM(value)}%`,
        },
        {
          formatter: (value) =>
            activeTab === "DetentionSpendTrend" ||
            activeTab === "FacilityTypeLocationShare"
              ? `$${formatToKOrM(value)}`
              : `${formatToKOrM(value)}`,
        },
        {
          formatter: (value) =>
            activeTab === "DetentionSpendTrend" ||
            activeTab === "FacilityTypeLocationShare"
              ? `$${formatToKOrM(value)}`
              : `${formatToKOrM(value)}`,
        },
      ],
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      markers: {
        width: 12,
        height: 12,
        radius: 10,
        shape: "circle",
      },
    },
    annotations: {
      yaxis: ["IncidentRateTrend", "DetentionSpendTrend"].includes(activeTab)
        ? [
            {
              y: benchmarkValue,
              yAxisIndex: 1,
              borderColor: "#B0B0B0",
              strokeDashArray: [5, 5],
              label: {
                borderColor: "#B0B0B0",
                style: {
                  color: "#B0B0B0",
                  padding: "20px",
                  fontSize: "10px",
                },
                text: `Benchmark = ${benchmarkValue}%`,
              },
            },
          ]
        : [],
    },
  };

  if (graphConfig?.[activeTab]?.rightYAxis) {
    options.yaxis = [...options?.yaxis, graphConfig?.[activeTab]?.rightYAxis];
  }
  return (
    <div className="grouped-bar-line-chart">
      <ReactApexChart
        options={options}
        series={slicedSeries}
        type="bar"
        height={350}
      />
      {totalPages > 1 && (
        <div className={classes.sliderContainer}>
          <div className={classes.sliderWrapper}>
            <div
              className={classes.progressTrack}
              style={{
                width: `${((currentPage + 1) / totalPages) * 100}%`,
                opacity: 0.3,
              }}
            />
            <input
              type="range"
              min="0"
              max={totalPages - 1}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
              className={classes.slider}
            />
          </div>
          <div className={classes.pageIndicator}>
            <span>{currentPage + 1}</span> / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default DetentionMultiLineBarChart;
